import { useMutation, useQuery, UseQueryOptions } from '@tanstack/react-query';
import { ContactRequest, ContactResponse, NoteRequest, NotesResponse } from './types';
import {
  createContact,
  createNote,
  deleteNote,
  getContact,
  getContacts,
  getNotes,
  updateContact,
  updateNote
} from './api';
import { queryClient } from 'providers/AllProviders';
import { MetaApiResponse } from 'utils/types/CommonApiResponse';
import { useBusiness } from 'providers/context/BusinessProvider';

function useResolvedBusinessId() {
  const { selectedBusiness } = useBusiness();
  return selectedBusiness != null ? String(selectedBusiness) : undefined;
}

export function useGetContacts(
  params?: object,
  options?: UseQueryOptions<{ data: ContactResponse[]; meta: MetaApiResponse }, Error>
) {
  const businessId = useResolvedBusinessId();

  return useQuery<{ data: ContactResponse[]; meta: MetaApiResponse }>({
    queryKey: ['contacts', businessId, params],
    queryFn: () => getContacts(params),
    enabled: Boolean(businessId),
    ...options
  });
}

export function useGetContact(id: number, options?: UseQueryOptions<ContactResponse, Error>) {
  const businessId = useResolvedBusinessId();

  return useQuery<ContactResponse>({
    queryKey: ['contact', businessId, id],
    queryFn: () => getContact(id!),
    enabled: Boolean(businessId && id),
    ...options
  });
}

export function useCreateContact() {
  const businessId = useResolvedBusinessId();

  return useMutation({
    mutationFn: (payload: ContactRequest) => createContact(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['contacts', businessId],
        refetchType: 'active'
      });
    }
  });
}

export function useUpdateContactMutation() {
  const businessId = useResolvedBusinessId();

  return useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: ContactRequest }) => updateContact(id, payload),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['contact', businessId, variables.id],
        refetchType: 'active'
      });
      queryClient.invalidateQueries({
        queryKey: ['contacts', businessId],
        refetchType: 'active'
      });
    }
  });
}

export function useGetNotes(
  id: number,
  options?: UseQueryOptions<{ data: NotesResponse[]; meta: MetaApiResponse }, Error>
) {
  const businessId = useResolvedBusinessId();

  return useQuery<{ data: NotesResponse[]; meta: MetaApiResponse }>({
    queryKey: ['notes', businessId, id],
    queryFn: () => getNotes(id!),
    enabled: Boolean(businessId && id),
    ...options
  });
}

export function useCreateNote(id: number) {
  const businessId = useResolvedBusinessId();

  return useMutation({
    mutationFn: (payload: NoteRequest) => createNote(id!, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['notes', businessId, id],
        refetchType: 'active'
      });
    }
  });
}

export function useUpdateNote(contactId: number) {
  const businessId = useResolvedBusinessId();

  return useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: NoteRequest }) => updateNote(contactId!, id!, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['notes', businessId, contactId],
        refetchType: 'active'
      });
    }
  });
}

export function useDeleteNote(contactId: number) {
  const businessId = useResolvedBusinessId();

  return useMutation({
    mutationFn: (noteId: number) => deleteNote(contactId!, noteId!),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['notes', businessId, contactId],
        refetchType: 'active'
      });
    }
  });
}
